<template>
    <div
        ref="cellRef"
        :key="column.key"
        :class="['pt-table-header_cell', { ['header-border']: headerBorder }]"
        :style="{
            width: column.children ? '' : typeof column.width === 'number' ? `${column.width}px` : column.width ?? '',
            flexGrow: column.grow ? Number(column.grow) : column.width ? 0 : 1,
            cursor: column.sorter ? 'pointer' : 'default',
        }"
        @click="changeCurrentSorter(column.key)"
    >
        <div
            :class="[
                'pt-table-header_cell_content',
                { 'pt-table-header_cell_content--sortable': column.sorter },
                column.class ? column.class : '',
            ]"
            :style="column.style ? column.style : {}"
        >
            <span>
                <slot>{{ column.name ?? column.key ?? '' }}</slot>

                <pt-tooltip v-if="column.helptip" effect="dark" placement="top" class="pt-table_help-icon">
                    <pt-icon icon="pt-icon-help-outline"></pt-icon>
                    <template #content>
                        <div style="max-width: 300px" v-html="column.helptip"></div>
                    </template>
                </pt-tooltip>
            </span>
            <div
                v-if="column.sorter"
                :class="['pt-table_sorter', { 'pt-table_sorter--active': currentSorter?.columnKey === column.key }]"
            >
                <div
                    :class="[
                        'pt-table_sorter-arrow pt-table_sorter-arrow--asc',
                        {
                            'pt-table_sorter-arrow--active':
                                currentSorter?.columnKey === column.key && currentSorter?.order === 'asc',
                        },
                    ]"
                ></div>
                <div
                    :class="[
                        'pt-table_sorter-arrow pt-table_sorter-arrow--desc',
                        {
                            'pt-table_sorter-arrow--active':
                                currentSorter?.columnKey === column.key && currentSorter?.order === 'desc',
                            'pt-table_sorter-arrow--preActive': currentSorter?.columnKey !== column.key,
                        },
                    ]"
                ></div>
            </div>
        </div>
        <div :class="['pt-table-header_cell_children_wrapper', column.children ? 'column-border' : '']">
            <template v-if="column.children">
                <HeaderCell
                    v-for="childColumn in column.children"
                    v-bind="$props"
                    :column="childColumn"
                    :key="childColumn.key ?? childColumn.name"
                >
                    <component :is="slots?.['header-' + childColumn.key]" />
                </HeaderCell>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'HeaderCell',
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue';
import throttle from 'lodash/throttle.js';
import ResizeObserver from 'resize-observer-polyfill';
import type { Props as TableProps, SetHeaderWidth, ChangeCurrentSorter, CurrentSorter } from '../table.vue';

interface Props {
    column: TableProps['columns'][number];
    columnWidth?: string;
    setHeaderWidth: SetHeaderWidth;
    currentSorter?: CurrentSorter;
    changeCurrentSorter: ChangeCurrentSorter;
    isFixedHeader?: boolean;
    isInPageMode: boolean;
    headerBorder: boolean;
}
const props = defineProps<Props>();
const slots = inject<Record<string, any>>('slots');

const cellRef = ref<HTMLDivElement>(null!);
const isFixedHeader = computed(
    () =>
        props.isFixedHeader ||
        (props.isInPageMode &&
            cellRef.value?.parentElement?.parentElement?.classList?.contains('pt-table-header-fixedWrapper')),
);

let ro: ResizeObserver | null = null;

const _calcHeadercellWidth = () => {
    if (props.column.children) return;
    if (!cellRef.value) return;
    if (isFixedHeader.value) return;
    if (ro) {
        ro.disconnect();
        ro = null as unknown as ResizeObserver;
    }
    const setHeaderWidth: ResizeObserverCallback = ([entry]) => {
        const cellWidth = entry.target.getBoundingClientRect().width;
        props.setHeaderWidth?.(props.column.key, `${cellWidth}px`);
    };

    const observerFunc = throttle(setHeaderWidth, 333);
    ro = new ResizeObserver(observerFunc);
    ro.observe(cellRef.value);
};

onMounted(_calcHeadercellWidth);
onUnmounted(() => {
    if (ro) {
        ro.disconnect();
        ro = null as unknown as ResizeObserver;
    }
});
</script>

<style lang="scss">
.pt-table-header_cell {
    position: relative;
    flex-shrink: 0;
    display: inline-flex;
    flex-direction: column;
    &.header-border {
        &:not(:last-of-type) {
            &::after {
                content: ' ';
                position: absolute;
                width: 1px;
                height: 100%;
                top: 0;
                right: 0;
                background-color: #dfe1e6;
            }
        }

        & > .pt-table-header_cell_children_wrapper.column-border {
            border-top: 1px solid #dfe1e6;
        }
    }
}

.pt-table-header_cell_children_wrapper {
    display: inline-flex;
}

.pt-table-header_cell_content {
    position: relative;
    flex-grow: 1;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    padding-left: 24px;
    padding-right: 24px;
    width: 100%;
    height: 56px;
    white-space: nowrap;
    background-color: #fafbfc;
    &--sortable {
        &:hover {
            background-color: #f2f7f2;
        }
    }
    &.white-background {
        background-color: #ffffff;
    }
    &:hover {
        & > .pt-table_sorter {
            opacity: 1;
            &--active {
                & > .pt-table_sorter-arrow:not(.pt-table_sorter-arrow--active) {
                    color: #5e6c84;
                }
            }
        }
    }
}

.pt-table_help-icon {
    width: 12px;
    height: 12px;
    fill: #8993a4;
    margin-left: 4px;
}
.pt-table_sorter {
    display: inline-flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    margin-left: 8px;
    height: 20px;
    opacity: 0;
    &--active {
        opacity: 1;
    }
}

.pt-table_sorter-arrow {
    border-width: 5px;
    border-style: solid;
    width: 10px;
    height: 10px;
    color: #c1c7d0;
    &--active {
        color: #00a92e;
    }

    &--preActive {
        color: #00a92e;
    }

    &--asc {
        border-color: transparent transparent currentColor transparent;
    }

    &--desc {
        border-color: currentColor transparent transparent transparent;
        margin-top: 4px;
    }
}
</style>
